// Utility functions for handling user presence consistently across components
import { 
  PRESENCE_COLORS, 
  PRESENCE_TITLES, 
  PRESENCE_STATES,
} from '../constants';

/**
 * Get the appropriate color for a user's presence status
 * @param {Object} presence - The presence record from useUserPresence
 * @param {boolean} isOnline - Whether the user is considered online
 * @returns {string} The hex color code for the presence indicator
 */
export const getPresenceColor = (presence, isOnline) => {
  if (!presence) return PRESENCE_COLORS.NO_DATA;

  if (presence?.presence === PRESENCE_STATES.UNKNOWN) {
    return PRESENCE_COLORS.UNKNOWN;
  }

  return isOnline ? PRESENCE_COLORS.ONLINE : PRESENCE_COLORS.OFFLINE;
};

/**
 * Get the appropriate title/tooltip for a user's presence status
 * @param {Object} presence - The presence record from useUserPresence
 * @param {boolean} isOnline - Whether the user is considered online
 * @returns {string} The tooltip text for the presence indicator
 */
export const getPresenceTitle = (presence, isOnline) => {
  if (!presence) return PRESENCE_TITLES.NO_DATA;

  if (presence?.presence === PRESENCE_STATES.UNKNOWN) {
    return PRESENCE_TITLES.LOADING;
  }

  return isOnline ? PRESENCE_TITLES.ONLINE : PRESENCE_TITLES.OFFLINE;
};

/**
 * Create presence helper functions for a specific user ID
 * Useful when you need to pass presence functions around that are bound to a specific user
 * @param {string} userId - The user ID to create helpers for
 * @param {Function} getPresence - The getPresence function from useUserPresence
 * @param {Function} isOnline - The isOnline function from useUserPresence
 * @returns {Object} Object with getColor and getTitle functions for the user
 */
export const createPresenceHelpers = (userId, getPresence, isOnline) => {
  return {
    getColor: () => getPresenceColor(getPresence(userId), isOnline(userId)),
    getTitle: () => getPresenceTitle(getPresence(userId), isOnline(userId)),
  };
};